import React from 'react';

// InfrastructureIllustration: SVG diagram showing the concept of an asset
// with concentric monitoring zones (core, protection, catchment, slope,
// downstream, access). Used in the "Critical risks often begin beyond the
// asset boundary" section.
export function InfrastructureIllustration() {
  return (
    <div className="relative aspect-[5/4] w-full rounded-2xl overflow-hidden border border-border bg-secondary/30" data-testid="infra-illustration">
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        backgroundImage: 'radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)',
        backgroundSize: '18px 18px',
      }} />
      <svg viewBox="0 0 500 400" xmlns="http://www.w3.org/2000/svg" className="relative w-full h-full">
        <defs>
          <linearGradient id="water-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary) / 0.35)" />
            <stop offset="100%" stopColor="hsl(var(--primary) / 0.15)" />
          </linearGradient>
          <pattern id="terrain" width="14" height="14" patternUnits="userSpaceOnUse">
            <path d="M0 14 Q7 6 14 14" stroke="hsl(var(--foreground) / 0.10)" fill="none" strokeWidth="1" />
          </pattern>
        </defs>

        {/* terrain background */}
        <rect width="500" height="400" fill="url(#terrain)" />

        {/* upstream catchment (large, dashed) */}
        <ellipse cx="260" cy="180" rx="210" ry="160" fill="none" stroke="hsl(var(--primary) / 0.35)" strokeDasharray="4 4" strokeWidth="1.2" />
        <text x="420" y="70" fontSize="11" fill="hsl(var(--muted-foreground))" fontFamily="var(--font-mono, monospace)">Upstream catchment</text>

        {/* reservoir protection buffer */}
        <ellipse cx="260" cy="200" rx="150" ry="115" fill="none" stroke="hsl(var(--primary) / 0.55)" strokeDasharray="3 3" strokeWidth="1.4" />
        <text x="370" y="120" fontSize="11" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">Protection buffer</text>

        {/* reservoir water body */}
        <path d="M170 145 Q220 120 280 128 Q340 138 360 175 Q370 210 320 235 Q265 255 205 240 Q160 225 155 195 Q150 170 170 145 Z" fill="url(#water-grad)" stroke="hsl(var(--primary) / 0.65)" strokeWidth="1.4" />
        <text x="260" y="195" fontSize="12" fill="hsl(var(--foreground) / 0.75)" textAnchor="middle" fontFamily="var(--font-mono, monospace)">Reservoir</text>

        {/* dam bar */}
        <rect x="140" y="230" width="38" height="12" fill="hsl(var(--primary))" rx="2" />
        <text x="55" y="242" fontSize="11" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">Dam & powerhouse</text>

        {/* downstream river */}
        <path d="M140 242 Q120 280 100 320 Q90 350 100 380" stroke="hsl(var(--primary) / 0.5)" strokeWidth="3" fill="none" strokeLinecap="round" />
        <text x="20" y="330" fontSize="11" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">Downstream</text>
        <text x="20" y="345" fontSize="11" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">impact zone</text>

        {/* access road */}
        <path d="M40 380 Q100 340 160 310 Q220 285 265 260" stroke="hsl(var(--foreground) / 0.55)" strokeWidth="1.6" fill="none" strokeDasharray="6 4" />
        <text x="250" y="330" fontSize="11" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">Access road corridor</text>

        {/* sensitive slope zone */}
        <path d="M330 90 L400 100 L410 155 L340 145 Z" fill="none" stroke="hsl(var(--primary) / 0.75)" strokeWidth="1.4" />
        <text x="345" y="85" fontSize="11" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">Sensitive slope</text>

        {/* Alert markers (Probable landslide, Encroachment, Fire) */}
        <AlertPin cx={378} cy={130} label="Probable landslide" color="amber" />
        <AlertPin cx={190} cy={165} label="Reservoir spread" color="blue" />
        <AlertPin cx={130} cy={340} label="Terrain disturbance" color="red" />

        {/* Satellite pass icon */}
        <g transform="translate(430, 32)">
          <circle cx="0" cy="0" r="14" fill="hsl(var(--background))" stroke="hsl(var(--primary))" strokeWidth="1.4" />
          <path d="M-6 -6 L6 6 M-6 6 L6 -6" stroke="hsl(var(--primary))" strokeWidth="1.2" />
        </g>
        <path d="M420 40 Q380 5 320 20 Q220 40 120 90 Q60 120 30 200" fill="none" stroke="hsl(var(--primary) / 0.35)" strokeWidth="1" strokeDasharray="2 3" />
      </svg>
    </div>
  );
}

function AlertPin({ cx, cy, label, color }) {
  const colors = {
    amber: 'hsl(38 92% 50%)',
    red: 'hsl(0 74% 55%)',
    blue: 'hsl(200 80% 45%)',
  };
  const c = colors[color] || colors.amber;
  return (
    <g>
      <circle cx={cx} cy={cy} r="8" fill={c} fillOpacity="0.20" />
      <circle cx={cx} cy={cy} r="4" fill={c} />
      <text x={cx + 10} y={cy + 4} fontSize="10" fill="hsl(var(--foreground))" fontFamily="var(--font-mono, monospace)">{label}</text>
    </g>
  );
}
